import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFavoriteQuestion, useToggleFavoriteQuestion } from '../../../../../hooks/useFavoriteQuestions';
import FileItem from '../../../../common/FileItem/FileItem';
import { chatModeType } from '../../.././../../types/Chat/Chat.types';
import { type UploadedFile } from '../../Input/Input';
import './User.css';

interface UserMessageProps {
    readonly text: string;
    readonly files: UploadedFile[];
    readonly chatMode: chatModeType;
    readonly interactionId?: string; // Optional - only available after backend processes the message
}

export const UserMessage = ({ text, files, interactionId, chatMode }: UserMessageProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const currentOrganization = userInfo?.currentOrganization;

    const isFavorite = useIsFavoriteQuestion();
    const toggleFavorite = useToggleFavoriteQuestion();
    const [isLoading, setIsLoading] = useState(false);

    const isFavoriteQuestion = currentOrganization ? isFavorite(text, currentOrganization, interactionId) : false;

    const handleToggleFavorite = async () => {
        if (!userInfo?.id || !currentOrganization || !text.trim()) return;

        setIsLoading(true);
        try {
            await toggleFavorite.mutateAsync({
                questionText: text,
                userId: userInfo.id,
                organizationId: currentOrganization,
                interactionId: interactionId,
            });
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-end w-full group">
            <div className="bg-gray-qm-100/70 dark:bg-gray-qm-900 p-3 rounded-2xl max-w-[80%] md:max-w-[70%] relative">
                {text && (
                    <div
                        className={`user-question-text ${files.length > 0 && 'mb-2'} ${
                            chatMode !== 'knowledge' && 'pr-8'
                        }`}
                        data-testid="user-message"
                    >
                        {text}
                    </div>
                )}
                {files.length > 0 && (
                    <div className="flex flex-col space-y-2">
                        {files.map((file, index) => (
                            <FileItem key={index} file={file} isLoading={false} />
                        ))}
                    </div>
                )}

                {/* Favorite button - only show when message has real interaction ID from backend */}
                {text && interactionId && (
                    <button
                        onClick={handleToggleFavorite}
                        disabled={isLoading}
                        className={`
                            absolute top-2 right-2 p-1 rounded-full transition-all duration-200
                            ${
                                isFavoriteQuestion
                                    ? 'opacity-100 text-primary-400'
                                    : 'opacity-100 text-gray-400 hover:text-primary-400'
                            }
                            hover:bg-gray-qm-200 dark:hover:bg-gray-qm-800
                            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        title={isFavoriteQuestion ? t('chat.favorites.remove') : t('chat.favorites.add')}
                        data-testid="favorite-button"
                    >
                        {chatMode !== 'knowledge' && (
                            <Bookmark
                                size={16}
                                className={`transition-all duration-200 ${isFavoriteQuestion ? 'fill-current' : ''}`}
                            />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default UserMessage;
